import { useEffect, useState } from "react";
import api from "@/lib/api";

function Sparkline({ points, stroke, testid }) {
    if (!points || points.length === 0) return null;
    const w = 220, h = 40, pad = 2;
    const max = Math.max(1, ...points);
    const xStep = (w - pad * 2) / Math.max(1, points.length - 1);
    const d = points.map((p, i) => {
        const x = pad + i * xStep;
        const y = h - pad - (p / max) * (h - pad * 2);
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block" data-testid={testid}>
            <path d={d} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
}

function StatTile({ label, value, sub, testid }) {
    return (
        <div className="border border-[color:var(--espresso)] p-4" data-testid={testid}>
            <p className="text-[0.55rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">{label}</p>
            <p className="font-serif text-3xl mt-1 text-[color:var(--soft-cream)]">{value}</p>
            {sub && <p className="text-[0.65rem] text-[color:var(--gold)] mt-1">{sub}</p>}
        </div>
    );
}

export default function CampaignAnalytics({ campaignId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!campaignId) return;
        let cancelled = false;
        setLoading(true);
        setError("");
        api.get(`/admin/campaigns/${campaignId}/analytics`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Analytics failed to load."); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [campaignId]);

    if (loading) return <p className="text-xs text-[color:var(--cream-muted)] mt-6" data-testid="campaign-analytics-loading">Loading analytics…</p>;
    if (error) return <p className="text-xs text-[color:var(--wine)] mt-6" data-testid="campaign-analytics-error">{error}</p>;
    if (!data) return null;

    const { counts, rates, by_day, unique_opens, unique_clicks } = data;
    const sent = counts.sent || 0;
    const opens = by_day.map((d) => d.opened);
    const clicks = by_day.map((d) => d.clicked);
    const totalEngagement = (counts.opened || 0) + (counts.clicked || 0);

    return (
        <div className="mt-6 pt-6 border-t border-[color:var(--espresso)]" data-testid="campaign-analytics">
            <div className="flex items-baseline justify-between mb-4">
                <p className="eyebrow">Performance</p>
                <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Live · powered by Resend webhooks</p>
            </div>
            {sent === 0 ? (
                <p className="text-sm text-[color:var(--cream-muted)]" data-testid="campaign-analytics-empty">
                    No sends yet. Once this campaign has fired, opens / clicks / bounces from the Resend webhook will appear here in real time.
                </p>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                        <StatTile label="Sent" value={sent} testid="analytics-sent" />
                        <StatTile label="Delivered" value={counts.delivered || (sent - counts.bounced)} sub={`${rates.delivery_rate}%`} testid="analytics-delivered" />
                        <StatTile label="Opens" value={counts.opened || 0} sub={`${unique_opens} unique · ${rates.open_rate}%`} testid="analytics-opens" />
                        <StatTile label="Clicks" value={counts.clicked || 0} sub={`${unique_clicks} unique · ${rates.click_rate}%`} testid="analytics-clicks" />
                        <StatTile label="Bounces" value={counts.bounced || 0} sub={`${rates.bounce_rate}%`} testid="analytics-bounces" />
                        <StatTile label="Unsubs" value={counts.unsubscribed || 0} testid="analytics-unsubs" />
                    </div>

                    {totalEngagement > 0 && (
                        <div className="mt-4 border border-[color:var(--espresso)] p-4">
                            <div className="flex items-center justify-between flex-wrap gap-4">
                                <div>
                                    <p className="text-[0.55rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Last 14 days · daily engagement</p>
                                    <div className="flex gap-4 mt-1 text-xs">
                                        <span className="text-[color:var(--gold)]">— Opens</span>
                                        <span className="text-[color:var(--soft-cream)]/70">— Clicks</span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <Sparkline points={opens} stroke="#c9a24a" testid="analytics-sparkline-opens" />
                                    <Sparkline points={clicks} stroke="#f5efe6" testid="analytics-sparkline-clicks" />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
